import { DevTool } from "@hookform/devtools";
import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "i18next";
import { FormProvider, useForm } from "react-hook-form";
import type { z } from "zod";
import { cn } from "~/utils";
import type { FormProps } from "~/utils/types";
import { Button } from "../ui/button";

const ReactHookForm = <T extends z.ZodType<any, any, any>>({
  onSubmit,
  schema,
  initialValues,
  children,
  onError,
  validate,
  submitText,
  className,
  disabled,
  encType,
}: FormProps<T>) => {
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema as any),
    defaultValues: initialValues,
    context: { schema },
  });

  const handleSuccess = async (values: z.infer<typeof schema>) => {
    await onSubmit(values, form.setError, form.formState);
  };

  const handleError = (errors: any) => {
    onError?.({
      errors: errors,
      values: form.getValues(),
    });
  };

  return (
    <FormProvider {...form}>
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          e.preventDefault();

          if (!validate) {
            return form.handleSubmit(handleSuccess, handleError)(e);
          }

          const validation = validate(form.getValues());

          if (typeof validation === "boolean") {
            return form.handleSubmit(handleSuccess, handleError)(e);
          }

          handleError(validation);

          form.clearErrors();

          for (const key in validation) {
            form.setError(key as any, {
              message: validation[key],
              type: "custom",
            });
          }
        }}
        className={cn("space-y-4", className)}
        noValidate
        encType={encType}
      >
        {process.env.NODE_ENV === "development" && <DevTool control={form.control} />}
        {children}
        {submitText && !disabled && (
          <Button type="submit" size="lg" isLoading={form.formState.isSubmitting}>
            {submitText || t("submit")}
          </Button>
        )}
      </form>
    </FormProvider>
  );
};

export { ReactHookForm };
